import { useMemo } from 'react';
import useAuthStore from '@pkg/auth/useStore';
import * as Designs from '@pkg/entities/designs';
import isEmpty from '@pkg/utils/objects/isEmpty';
import getOrgFeatures from '../organisations/get/features';
import getOrgPermissions from '../organisations/get/permissions';
import getOrgPolicyLevel from '../organisations/get/policyLevel';
import usePolicies from '../policies/usePolicies';
import useCanAct from './can/useAct';
import useCanCreate from './can/useCreate';
import useCanGroupRoles from './can/useGroupRoles';
import getModel from './get/model';
import getPermissions from './get/permissions';
import useCombined from './use/combined';
import useCheckActions from './useCheckActions';
import useRelationships from './useRelationships';

export const DEFAULT_ACCESS = Object.freeze({
  isReady: false,
  isRestricted: true,
  can: () => false,
  canMany: () => false,
  canCreate: () => false,
  features: {},
  model: {
    budget: false, // can see budgets
    edit: false, // can modify design
    manage: false, // can manage organisation
    publish: false, // can publish the current scenario design
    restore: false, // can restore to the current historical event
  },
});

/**
 * @param {Object} design
 * @param {Object} [snapshot]
 * @param {Boolean} [isHistorical]
 * @returns {Object}
 */
export default function useAccess(design, snapshot, isHistorical = true) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const organisation = useAuthStore((state) => state.organisation);
  const me = useAuthStore((state) => state.me);
  const assignedFeatures = useAuthStore((state) => state.features);

  const organisationId = organisation?.uuid;
  const designId = design?.uuid;
  const isScenario = design?.is_scenario ?? false;

  const main = Designs.useStore((state) => state.main);
  const hydrated = Designs.useHydratedDesign(main);
  const mainSnapshot = hydrated?.latest?.snapshot;
  const combinedSnapshot = useCombined(snapshot, mainSnapshot, design?.scope);

  const { lookup, isLoading } = usePolicies();
  const level = getOrgPolicyLevel(me, organisationId);
  const relationships = useRelationships(me, design, hydrated, snapshot);

  const features = getOrgFeatures(assignedFeatures);
  const canCreate = useCanCreate(
    me,
    level,
    isHistorical,
    combinedSnapshot,
    lookup
  );
  const canGroupTheseRoles = useCanGroupRoles(
    me,
    level,
    isHistorical,
    relationships
  );

  return useMemo(() => {
    if (!isAuthenticated) {
      return { ...DEFAULT_ACCESS, isReady: true };
    }

    const permissions = {
      ...getPermissions(designId),
      ...getOrgPermissions(organisationId),
    };

    const isReady =
      !isLoading && !isEmpty(relationships.current) && Boolean(design);
    const isRestricted = organisation.is_restricted;

    const model = getModel(me, permissions, isScenario, isHistorical);
    const check = useCheckActions(level, isScenario, lookup, relationships);
    const { one, many } = useCanAct(model, check, isHistorical);

    return Object.freeze({
      isReady,
      isRestricted,
      can: one,
      canMany: many,
      canCreate,
      canGroupTheseRoles,
      features,
      model,
    });
  }, [lookup, relationships, design?.uuid, isLoading, isAuthenticated]);
}
